import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useUserStore } from '@/store/modules/user';
import { loginRenewal } from '@/api/auth';
import { setUdbAuthCode, getUdbAuthCode, getPoolNo } from '@/utils/auth'

// 刷新标记
let isRefreshing = false;
// 重试队列
let retryQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
  config: AxiosRequestConfig;
}> = [];

// 创建axios实例
const service: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  },
  paramsSerializer: params => qs.stringify(params, { arrayFormat: 'repeat' })
});

// 请求拦截器
service.interceptors.request.use(
  (config: AxiosRequestConfig) => {
    // 添加token
    if (getUdbAuthCode()) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${getUdbAuthCode()}`;
    }
    
    // 添加应用ID
    config.headers = config.headers || {};
    
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// 响应拦截器
service.interceptors.response.use(
  (response: AxiosResponse) => {
    const res = response.data;
    // 根据业务code处理
    if (res.code === 0 || res.code === 200) {
      return res.data;
    } else if (res.code === 401) {
      return handleUnauthorized(response.config);
    } else {
      ElMessage.error(res.msg || '请求失败');
      return Promise.reject(new Error(res.msg || '请求失败'));
    }
  },
  error => {
    console.error('请求错误:', error);
    if (error.response?.status === 401) {
      return handleUnauthorized(error.config);
    } else if (error.response?.status === 403) {
      ElMessage.error('没有权限访问');
    } else if (error.message.includes('timeout')) {
      ElMessage.error('请求超时，请检查网络连接');
    } else if (error.message.includes('Network Error')) {
      ElMessage.error('网络错误，请检查网络连接');
    } else {
      ElMessage.error(error.message || '服务器错误');
    }
    return Promise.reject(error);
  }
);

// 处理队列中的请求
const processQueue = (error: any, token: string | null = null) => {
  retryQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      service(promise.config).then(promise.resolve).catch(promise.reject);
    }
  });
  retryQueue = [];
};

// 统一的401处理函数
const handleUnauthorized = (config: AxiosRequestConfig): Promise<any> => {
  // 如果是刷新token的请求失败，直接退出
  if (config.url?.includes('/oauth2/renewal')) {
    forceLogout('登录已过期，请重新登录');
    return Promise.reject(new Error('刷新token失败'));
  }
  
  // 如果已经在刷新token，将当前请求加入队列
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      retryQueue.push({ resolve, reject, config });
    });
  }
  
  isRefreshing = true;
  
  return new Promise((resolve, reject) => {
    // 尝试刷新token
    loginRenewal()
      .then((response: any) => {
        if (response.code === 200 || response.code === 0) {
          // 刷新成功，更新token
          const newToken = response.data.token;
          setUdbAuthCode(newToken);
          
          // 更新当前请求的token
          if (config.headers) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          
          // 处理队列中的请求
          processQueue(null, newToken);
          
          // 重新发送当前请求
          service(config).then(resolve).catch(reject);
        } else {
          // 刷新token接口返回错误
          forceLogout(response.msg || '刷新token失败');
          reject(new Error('刷新token失败'));
        }
      })
      .catch((error: any) => {
        // 刷新token失败，强制退出
        console.error('刷新token失败:', error);
        forceLogout('登录已过期，请重新登录');
        processQueue(error, null);
        reject(error);
      })
      .finally(() => {
        isRefreshing = false;
      });
  });
};

// 强制退出登录
const forceLogout = (message: string) => {
  const userStore = useUserStore()
  var poolNo = ''
  const poolNoCache = getPoolNo()
  if (userStore.getPoolNo) {
    poolNo = userStore.getPoolNo;
  } else if (poolNoCache) {
    poolNo = poolNoCache;
  }
  const redirectUrl = '/login' + (poolNo ? `?poolNo=${poolNo}` : '');
  
  // 清除本地存储
  userStore.clearAuthData();
  
  // 只在第一次时弹窗
  if (!isRefreshing) {
    ElMessageBox.confirm(message, '提示', {
      confirmButtonText: '重新登录',
      showCancelButton: false,
      type: 'warning',
      closeOnClickModal: false,
      closeOnPressEscape: false,
      beforeClose: (action, instance, done) => {
        if (action === 'confirm') {
          window.location.href = redirectUrl;
          done();
        }
      }
    });
  }
};

export default service;
